// 🔧 passport_achievements.js
// ✅ Purpose: Display attendee achievements earned through the LGV Engagement Engine
// ✅ Features:
// - Lists unlocked achievements
// - Shows icon, title and description
// - Foundation for future locked achievements
// - Uses Engagement Engine achievement records
// - Responsive Bootstrap layout
// - Fully block-commented for future development

import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { recordEngagement } from "../../services/engagement/engagement_tracker";
import { ENGAGEMENT_EVENTS } from "../../services/engagement/engagement_event_types";
import { logger } from "../../utils/logger";
import { auth, db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";

const PassportAchievements = () => {

  const {
    ready,
    eventLoading,
    eventId,
    eventslug,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const [loading, setLoading] = useState(true);
  const [achievements, setAchievements] = useState([]);
    const [achievementViewRecorded, setAchievementViewRecorded] = useState(false);

  useEffect(() => {
    const loadAchievements = async () => {
      if (!eventId || !auth.currentUser) {
        return;
      }

      setLoading(true);

      const snap = await getDocs(
        query(
          collection(
            db,
            "events",
            eventId,
            "engagement_achievements"
          ),
          where("userId", "==", auth.currentUser.uid)
        )
      );

      setAchievements(
        snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
      );

      setLoading(false);
    };

    loadAchievements();
  }, [eventId]);

  useEffect(() => {
    if (
        !ready ||
        eventLoading ||
        !eventId ||
        achievementViewRecorded
    ) {
        return;
    }

    const recordAchievementView = async () => {
        try {
        await recordEngagement({
            eventType: ENGAGEMENT_EVENTS.ACHIEVEMENTS_VIEWED,
            eventId,
            uid: auth.currentUser?.uid || null,
            source: "passport",
            metadata: {
            page: "passport_achievements",
            path: window.location.pathname,
            },
        });

        setAchievementViewRecorded(true);
        } catch (err) {
        logger.warn("⚠ Failed to record achievements view:", {
            value: err,
        });
        }
    };

    recordAchievementView();
    }, [
    ready,
    eventLoading,
    eventId,
    achievementViewRecorded,
    ]);

  if (!ready || eventLoading || loading) {
    return (
      <FullPageSpinner message="Loading achievements..." />
    );
  }

  return (
    <div className="container py-4" style={{ maxWidth: 900 }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            🏅 My Achievements
          </h2>

          <p className="text-muted mb-0">
            Unlock achievements while exploring this event.
          </p>
        </div>

        <Link
          className="btn btn-outline-secondary"
          to={`/${eventslug}/passport`}
        >
          ← Passport
        </Link>
      </div>

      <div className="alert alert-success">
        <strong>
          {achievements.length}
        </strong>{" "}
        achievements unlocked.
      </div>

      {achievements.length === 0 ? (
        <div className="card shadow-sm">
          <div className="card-body text-center py-5">
            <h5>No achievements yet</h5>

            <p className="text-muted mb-0">
              Complete Passport activities to unlock your first badge.
            </p>
          </div>
        </div>
      ) : (
        <div className="row g-4">
          {achievements.map((achievement) => (
            <div
              key={achievement.id}
              className="col-md-6"
            >
              <div className="card shadow-sm h-100">
                <div className="card-body">
                  <div
                    className="display-3 mb-3"
                    style={{
                      lineHeight: 1,
                    }}
                  >
                    {achievement.icon}
                  </div>

                  <h5>
                    {achievement.title}
                  </h5>

                  <p className="text-muted">
                    {achievement.description}
                  </p>

                  <div className="small text-success">
                    ✔ Achievement unlocked
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PassportAchievements;